import type { WorkspaceBranding } from './branding/branding';
import type { FormSubmissionView } from './forms';
// Wire types shared by the API, web app and (future) React Native apps.
// Field names mirror the database (snake_case) so rows pass through unchanged.

export type UUID = string;
export type ISODateTime = string; // e.g. 2026-10-11T09:00:00.000Z
export type ISODate = string; // e.g. 2026-10-11

export type WorkspaceRole = 'owner' | 'admin' | 'member';
export type WorkforceRole = 'employee' | 'supervisor' | 'manager' | 'admin';

export interface Profile {
  user_id: UUID;
  email: string | null;
  display_name: string;
  avatar_path: string | null;
  avatar_url?: string | null;
  job_title: string | null;
  phone: string | null;
  timezone: string;
  status_text: string | null;
  status_emoji: string | null;
  status_expires_at: ISODateTime | null;
  /** Birthday (day + month only, never the year). Only returned on /me. */
  birthday_day?: number | null;
  birthday_month?: number | null;
  birthday_visibility?: 'hidden' | 'day_only' | 'visible';
  /** #27: language to show other people's content in (null = don't translate), and whether to do it automatically. */
  preferred_language?: string | null;
  auto_translate?: boolean;
  /** 12- or 24-hour clock (default 24h). Only returned on /me. */
  clock_format?: '24h' | '12h';
}

export interface Workspace {
  id: UUID;
  name: string;
  slug: string | null;
  timezone: string;
  role: WorkspaceRole;
  /** t374u: the company's name, colours and logos (The Hub's when it has set none). */
  branding?: WorkspaceBranding;
  /** t555u: minutes away before the iOS app's Face ID lock asks again (0 = every time; default 60). */
  app_lock_after_min?: number;
}

export interface WorkspaceMember extends Profile {
  workspace_id: UUID;
  role: WorkspaceRole;
  status: 'active' | 'deactivated';
  joined_at: ISODateTime;
  presence?: PresenceStatus;
  /** Only with ?detail=true for admins (UI review): roles granted to the person directly, any scope. */
  role_names?: string[];
  /** Only with ?detail=true: self-employed driver (fleet.is_external). */
  is_external?: boolean;
  /** Only with ?detail=true and "See sign-in history": when they last used The Hub (#55). */
  last_active_at?: ISODateTime | null;
  /** Only with ?detail=true (t425u): a profile added by an import that can't sign in yet (invite them from Users). */
  no_sign_in?: boolean;
}

/** t471u: the placeholder a permanently deleted person's kept messages and records point at. */
export interface DeletedMember { user_id: UUID; display_name: string }

export interface Me {
  profile: Profile;
  workspaces: Workspace[];
  /** t437u: the LiveKit server calls connect to (wss://…), so apps can warm the connection before joining. Null when calls aren't set up. */
  livekit_url?: string | null;
}

export interface Team {
  id: UUID;
  workspace_id: UUID;
  name: string;
  description: string | null;
  location_id: UUID | null;
  /** 'department' = admin-managed team; 'group' = user-defined group. */
  kind: 'department' | 'group';
  created_by: UUID | null;
  member_count?: number;
  members?: { user_id: UUID; role: 'lead' | 'member'; display_name: string }[];
  archived_at?: ISODateTime | null;
}

/** One person in the staff list ("Add from staff list" on Teams & groups, Rooms). */
export interface StaffDirectoryEntry {
  user_id: UUID;
  display_name: string;
  job_title: string | null;
  /** Only where you can edit this person's employment; otherwise null. */
  employment_type: 'full_time' | 'part_time' | 'zero_hours' | 'casual' | null;
  location_ids: UUID[];
  position_ids: UUID[];
  /** Active departments and groups they're in. */
  team_ids: UUID[];
}

export interface Location {
  id: UUID;
  workspace_id: UUID;
  name: string;
  code: string | null;
  address: string | null;
  timezone: string;
  archived_at: ISODateTime | null;
  /** Buildings are locations (backlog #12). */
  kind?: BuildingKind;
  description?: string | null;
}

/** Building type key (collab.building_types.key); built-ins are warehouse, store, office, site, other. */
export type BuildingKind = string;

export interface BuildingSummary {
  id: UUID; workspace_id: UUID; name: string; code: string | null; address: string | null; timezone: string;
  kind: BuildingKind; description: string | null;
  /** Set in the Archived buildings list (#60). */
  archived_at?: ISODateTime | null;
  /** feedback #6: from the building's type */
  type_name?: string; icon?: import('./buildingTypes').BuildingIconName; capabilities?: import('./buildingTypes').BuildingCapability[];
  follower_count: number; following: boolean; chat_count: number; open_task_count: number;
}

export interface BuildingActivity {
  type: 'message' | 'task';
  id: string;
  at: ISODateTime;
  actor_id: UUID | null;
  text: string;
  conversation_id?: UUID; conversation_slug?: string;
  /** Task events (t122u): the raw change record, formatted on the client with `activity_refs`. */
  changes?: Record<string, unknown> | null;
  action?: string; task_id?: UUID; task_key?: string;
}

export interface BuildingDetail extends Omit<BuildingSummary, 'chat_count' | 'open_task_count'> {
  archived_at: ISODateTime | null;
  can_manage: boolean;
  chats: { id: UUID; kind: ConversationKind; slug: string | null; title: string | null; description: string | null; is_private: boolean; last_message_at: ISODateTime | null; is_member: boolean; member_count: number }[];
  tasks: { id: UUID; key: string; title: string; project_id: UUID; project_name: string; priority: TaskPriority; due_date: ISODate | null; completed_at: ISODateTime | null; status_name: string; status_color: string; status_category: StatusCategory; assignee_ids: UUID[]; parent_task_id?: UUID | null; area_name?: string | null }[];
  projects: { id: UUID; key: string; name: string; color: string; open_task_count: number }[];
  followers: { user_id: UUID; display_name: string }[];
  /** null = caller doesn't manage this building's rota. */
  shifts_today: { id: UUID; user_id: UUID | null; display_name: string | null; starts_at: ISODateTime; ends_at: ISODateTime; status: string; open_slots: number; position_name: string | null; position_color: string | null }[] | null;
  activity: BuildingActivity[];
  activity_refs?: ActivityRefs;
  /** t220u: clocked in here now; null = caller can't see who's in (buildings.view_shifts). */
  in_now?: BuildingInNow[] | null;
}

/** t220u: someone clocked in at a building now. */
export interface BuildingInNow { user_id: UUID; display_name: string; clock_in_at: ISODateTime; position_name: string | null; on_break: boolean }

/** t220u: GET /workspaces/:ws/buildings/status, the phone Buildings list's quick status per live building. */
export interface BuildingStatus {
  id: UUID;
  /** One of the buildings in your work details; `primary` is your primary one. */
  mine: boolean; primary: boolean;
  /** Open issues you can see there (all of them with view_all / manage there, else yours). */
  issues: { open: number; critical: number; breached: number; can_view_all: boolean };
  /** Clocked in there now; null = you can't see who's in. */
  in_now: number | null;
  /** Today's checklist runs you can see there. */
  checklists: { total: number; done: number; missed: number };
}

export type ConversationKind = 'dm' | 'group_dm' | 'channel' | 'entity' | 'room';

export interface Conversation {
  id: UUID;
  workspace_id: UUID;
  kind: ConversationKind;
  title: string | null;
  slug: string | null;
  description: string | null;
  topic: string | null;
  is_private: boolean;
  project_id: UUID | null;
  archived_at: ISODateTime | null;
  last_message_at: ISODateTime | null;
  last_sequence: number;
  last_read_sequence: number;
  unread_count: number;
  mention_count: number;
  is_member: boolean;
  notify_level: 'all' | 'mentions' | 'none';
  members?: ConversationMember[];
  member_count?: number;
  /** Chat list only (GET /workspaces/:ws/conversations): the latest top-level message, for conversations you can read. */
  last_message?: import('./chatPreview').ChatLastMessage | null;
  /** Navigation only (backlog #3); never grants access. */
  section_id?: UUID | null;
  section_position?: number;
  /** Building the chat is allocated to (backlog #12). */
  location_id?: UUID | null;
  /** Form-template channel (backlog #13): top-level posts must use this form. */
  form_template_id?: UUID | null;
  /** Backlog #14: who may start polls and surveys here */
  poll_policy?: 'members' | 'moderators';
  /** UI review: channel defaults (translation, thread view, acknowledgement pre-fill). */
  settings?: import('./people').ChannelSettings;
  /** Caller may rename, archive and change channel defaults (chat.channel.manage here, or the channel's Manage rule). */
  can_manage?: boolean;
  /** t520u: what the caller may do here (channels; GET /conversations/:id). Missing = whatever their role allows. */
  my_permissions?: import('./channelPermissions').ChannelActionSet;
  /** t520u: someone has customised this channel's permissions. */
  permissions_customised?: boolean;
  /**
   * t722u: the project this channel follows for private or open (its "Who can see this project" setting). Set, the
   * channel's Private switch is changed in the project's Settings, not here. Channels only (GET /conversations/:id).
   */
  privacy_project?: { id: UUID; name: string } | null;
  /** Rooms (#54): archived_at is when it was closed. */
  created_by?: UUID | null;
  closed_by?: UUID | null;
  closed_by_name?: string | null;
  members_can_add?: boolean;
  can_close?: boolean;
  can_add_members?: boolean;
}

/** Closed ad-hoc meeting rooms filters (#54): from/to are inclusive UK days on the closed or created date. */
export interface ClosedRoomsFilter { q?: string; field?: 'closed' | 'created'; from?: string; to?: string; created_by?: string; limit?: number; offset?: number }
export interface ClosedRoomsPage { rooms: ClosedRoom[]; total: number; creators: { user_id: string; display_name: string }[] }
export interface ClosedRoom {
  id: UUID; title: string; created_at: ISODateTime; closed_at: ISODateTime; created_by: UUID | null; last_message_at: ISODateTime | null;
  created_by_name: string | null; closed_by_name: string | null; member_count: number; is_member: boolean; can_reopen: boolean;
}

export interface RoomShiftGroup { key: string; location_id: string; label: string; user_ids: UUID[]; count: number }

export interface ChatSection {
  id: UUID;
  parent_id: UUID | null;
  name: string;
  position: number;
}

export interface ConversationMember {
  user_id: UUID;
  display_name: string;
  avatar_path: string | null;
  role: 'owner' | 'admin' | 'member';
  last_read_sequence: number;
}

export interface AttachmentSummary {
  id: UUID;
  file_name: string;
  content_type: string;
  size_bytes: number;
  width?: number | null;
  height?: number | null;
}

/** Signed read URL for a file inside an announcement / article; images also get a resized thumb_url (feedback #7). */
export interface FeatureFileUrl { url: string; thumb_url?: string | null; file_name: string; content_type: string; width?: number | null; height?: number | null; expires_in: number }

export interface ReactionSummary {
  emoji: string;
  count: number;
  user_ids: UUID[];
}

export interface Reactor { user_id: UUID; display_name: string }
/** POST /workspaces/:ws/reactors: item id -> emoji -> reactors (oldest first). Items you can't see are omitted. */
export interface ReactorsResponse { messages: Record<UUID, Record<string, Reactor[]>>; kudos: Record<UUID, Record<string, Reactor[]>> }

export interface Message {
  id: UUID;
  conversation_id: UUID;
  sender_id: UUID;
  client_message_id: UUID;
  sequence: number;
  body: string;
  kind: 'user' | 'system';
  parent_id: UUID | null;
  mentions: UUID[];
  metadata: Record<string, unknown>;
  reply_count: number;
  last_reply_at: ISODateTime | null;
  created_at: ISODateTime;
  edited_at: ISODateTime | null;
  deleted_at: ISODateTime | null;
  attachments?: AttachmentSummary[];
  reactions?: ReactionSummary[];
  pinned?: boolean;
  /** Present on form posts in form-template channels (backlog #13). */
  form?: FormSubmissionView;
  /** t518u: a top-level post marked complete (e.g. when its linked journey reached the stop). */
  completed_at?: ISODateTime | null;
  completed_by?: UUID | null;
  /** t518u: the journey created from this post (form channels linked to journeys). */
  linked_journey?: LinkedJourneySummary | null;
}

export interface LinkedJourneySummary { id: UUID; ref: string; status: string; planned_start: ISODateTime }

export interface MessagePage {
  messages: Message[];
  /** Sequence to pass as `before` for the next older page, or null at the beginning. */
  next_before: number | null;
  /** For after_seq recovery: true when more messages exist after the last returned. */
  has_more_after?: boolean;
  /** Highest allocated sequence in the conversation at query time (gap-detection baseline). */
  latest_sequence: number;
}

export interface SendMessageInput {
  client_message_id: UUID;
  body: string;
  parent_id?: UUID | null;
  mentions?: UUID[];
  attachment_ids?: UUID[];
  /** Sent from the in-call chat panel (t133u); the server stamps metadata.call_id if that call is live here. */
  call_id?: UUID | null;
}

export type PresenceStatus = 'online' | 'away' | 'offline';

export interface PresenceEntry {
  user_id: UUID;
  status: PresenceStatus;
  last_seen_at: ISODateTime | null;
}

export interface AttachmentUpload {
  attachment: AttachmentSummary & { status: 'pending' | 'ready' };
  upload_url: string;
  /** Supabase Storage signed-upload token (PUT the file to upload_url). */
  token: string;
}

export interface NotificationItem {
  id: UUID;
  workspace_id: UUID;
  kind: string;
  title: string;
  body: string | null;
  entity_type: string | null;
  entity_id: UUID | null;
  link: string | null;
  actor_id: UUID | null;
  data: Record<string, unknown>;
  read_at: ISODateTime | null;
  created_at: ISODateTime;
}

/** t189u: every kind the search covers (Help Centre guides are searched in the browser, from the bundled index). */
export const SEARCH_TYPES = ['messages', 'tasks', 'people', 'announcements', 'articles', 'policies', 'issues', 'channels', 'buildings', 'events'] as const;
export type SearchKind = typeof SEARCH_TYPES[number];
/** t303u: result order. Relevance (rank, then newest) is the default; dates apply to dated kinds, the rest stay A–Z. */
export const SEARCH_SORTS = ['relevance', 'newest', 'oldest'] as const;
export type SearchSort = typeof SEARCH_SORTS[number];
/** t303u: kinds with a date (sort and the date range apply), kinds with a From person, and kinds with an In conversation. */
export const SEARCH_DATED: readonly SearchKind[] = ['messages', 'tasks', 'announcements', 'issues', 'articles'];
export const SEARCH_FROM: readonly SearchKind[] = ['messages', 'tasks', 'announcements', 'issues'];
export const SEARCH_IN: readonly SearchKind[] = ['messages'];
/**
 * t303u: search options. `after` (inclusive) and `before` (exclusive) are instants (the London days of the date range);
 * `from` is a person (message author, task creator or assignee, issue reporter, announcement author); `in` a
 * conversation you're a member of. `offset` / `limit` page one kind ("Show more").
 */
export interface SearchOptions {
  type?: SearchKind; sort?: SearchSort; after?: ISODateTime; before?: ISODateTime; from?: UUID; in?: UUID; offset?: number; limit?: number;
}
export interface SearchResult {
  messages: (Message & { conversation_title: string | null; conversation_kind: ConversationKind; headline: string })[];
  tasks: (TaskSummary & { project_name: string; headline: string; created_at?: ISODateTime; created_by?: UUID })[];
  people: (Profile & { location_name?: string | null })[];
  announcements: { id: UUID; title: string; author_id: UUID; author_name: string | null; publish_at: ISODateTime; headline: string }[];
  articles: { id: UUID; title: string; collection_name: string | null; snippet: string | null; published_at?: ISODateTime | null }[];
  /** t612u: published policies you can read (Policies module). Optional so older servers still parse. */
  policies?: { id: UUID; title: string; type_name: string; version: number; snippet: string | null; published_at: ISODateTime | null }[];
  issues: { id: UUID; ref: number; title: string; status: string; location_name: string | null; reporter_id?: UUID; created_at?: ISODateTime }[];
  channels: { id: UUID; slug: string | null; title: string | null; description: string | null; is_private: boolean; is_member: boolean }[];
  buildings: { id: UUID; name: string; code: string | null; address: string | null }[];
  /** t213u: calendar events you can see (the calendar's own rule), upcoming first. Local start 'YYYY-MM-DDTHH:mm'. */
  events?: { id: UUID; title: string; starts_local: string; all_day: boolean; scope: string; location_text: string | null; recurring: boolean }[];
  /** t303u: kinds with more results after this page (Show more asks for the next page of that kind). */
  more?: Partial<Record<SearchKind, boolean>>;
}

// ---------------------------------------------------------------------------
// Calls
// ---------------------------------------------------------------------------
export type CallStatus = 'ringing' | 'active' | 'ended';
export interface Call {
  id: UUID;
  workspace_id: UUID;
  conversation_id: UUID | null;
  project_id: UUID | null;
  room_name: string;
  media: 'audio' | 'video';
  status: CallStatus;
  started_by: UUID;
  created_at: ISODateTime;
  started_at: ISODateTime | null;
  ended_at: ISODateTime | null;
  participants?: { user_id: UUID; status: string; display_name: string }[];
}
export interface CallJoinInfo {
  call: Call;
  token: string;
  url: string;
}

// ---------------------------------------------------------------------------
// Projects & tasks
// ---------------------------------------------------------------------------
export type TaskPriority = 'none' | 'low' | 'medium' | 'high' | 'urgent';
export type StatusCategory = 'backlog' | 'todo' | 'in_progress' | 'done' | 'cancelled';

export interface ProjectStatus {
  id: UUID;
  project_id: UUID;
  name: string;
  color: string;
  category: StatusCategory;
  position: number;
}

export interface Milestone {
  id: UUID;
  project_id: UUID;
  name: string;
  description: string | null;
  due_date: ISODate | null;
  status: 'open' | 'completed';
}

export type CustomFieldType = 'text' | 'number' | 'date' | 'select' | 'multi_select' | 'checkbox' | 'user' | 'url';
export interface CustomField {
  id: UUID;
  project_id: UUID;
  name: string;
  field_type: CustomFieldType;
  options: { value: string; label: string; color?: string }[];
  position: number;
  required: boolean;
}

export interface Project {
  id: UUID;
  workspace_id: UUID;
  key: string;
  name: string;
  description: string | null;
  color: string;
  icon: string | null;
  visibility: 'workspace' | 'private';
  conversation_id: UUID | null;
  team_id: UUID | null;
  location_id?: UUID | null;
  archived_at: ISODateTime | null;
  my_role: 'owner' | 'editor' | 'viewer' | null;
  task_count?: number;
  open_task_count?: number;
}

export interface ProjectDetail extends Project {
  /** From tasks.project.edit / tasks.project.manage at this project (membership roles, scoped or workspace grants). */
  can_edit?: boolean;
  can_admin?: boolean;
  statuses: ProjectStatus[];
  milestones: Milestone[];
  custom_fields: CustomField[];
  members: { user_id: UUID; role: 'owner' | 'editor' | 'viewer'; display_name: string }[];
}

export interface TaskSummary {
  id: UUID;
  project_id: UUID;
  number: number;
  key: string;
  title: string;
  status_id: UUID;
  priority: TaskPriority;
  parent_task_id: UUID | null;
  milestone_id: UUID | null;
  start_date: ISODate | null;
  due_date: ISODate | null;
  completed_at: ISODateTime | null;
  /** t263u: who completed it (set by the API with completed_at; null for older tasks with no history). */
  completed_by?: UUID | null;
  position: number;
  version: number;
  assignee_ids: UUID[];
  /** Teams/groups assigned; membership is resolved dynamically. */
  team_ids: UUID[];
  subtask_count: number;
  subtask_done_count: number;
  comment_count: number;
  recurrence: RecurrenceRule | null;
  location_id?: UUID | null;
  /** t118: an area inside the building, and an optional budget in pence. */
  area_id?: UUID | null;
  area_name?: string | null;
  budget_pence?: number | null;
  /** True for a task with no project (backlog #47); project_id is then the hidden container. */
  no_project?: boolean;
  /** Private task (was a personal to-do): only its creator can see it, managers included. */
  is_private?: boolean;
  /** t171u: set for a bug report or feature suggestion (Hub feedback project). */
  feedback_kind?: 'bug' | 'feature' | null;
  due_time?: string | null;
  reminder_minutes?: number | null;
  remind_at?: ISODateTime | null;
  /** "Remind me about this task": the task this private reminder is about. */
  reminder_for_task_id?: UUID | null;
  updated_at: ISODateTime;
  /**
   * t293u: list rows only (Tasks area, project list). unread = new to you, or changed / commented on by someone else
   * since you last opened it (collab.item_reads, from the t293u migration on).
   */
  unread?: boolean;
  attachment_count?: number;
  /** Your private "Remind me about this task" reminder, if you set one. */
  my_reminder_at?: ISODateTime | null;
  team_names?: string[];
  /** Made from a chat message. The chat is named (source_label: "#general", the other person in a DM, a group's names) and linked only if you're in it. */
  source_message_id?: UUID | null;
  source_conversation_id?: UUID | null;
  source_conversation_kind?: string | null;
  source_label?: string | null;
}

/** A row in the Tasks area (GET /workspaces/:ws/tasks). */
export interface TaskListItem extends TaskSummary {
  project_name: string;
  project_color: string;
  status_name: string;
  status_color: string;
  status_category: string;
  created_by: UUID;
  location_name: string | null;
}
/** t333u: a subtask row under an expanded task in a list (GET /workspaces/:ws/tasks/subtasks). */
export type SubtaskItem = TaskSummary & { status_name: string; status_color: string; status_category: StatusCategory };
export type TaskListFilter = { project?: 'all' | 'none' | UUID; scope?: 'mine' | 'created' | 'all'; status?: 'open' | 'done' | 'all'; location_id?: UUID; visibility?: 'private' | 'shared'; unread?: boolean };

export interface RecurrenceRule {
  freq: 'daily' | 'weekly' | 'monthly';
  interval: number;
  /** ISO weekdays 1..7 for weekly; monthly "nth weekday" uses the first one. */
  byweekday?: number[];
  until?: ISODate;
  /** t335u: monthly on a day of the month (1..31, -1 = the last day). Default: the due date's day. */
  bymonthday?: number;
  /** t335u: monthly on the nth `byweekday[0]` (1..4, -1 = the last). Set instead of bymonthday. */
  bysetpos?: number;
  /** t335u: stop after this many in all (the first one counts). */
  count?: number;
  /** t335u: this instance's number in the series (1 = the first); carried forward to apply `count`. */
  seq?: number;
  /** t335u: when the next one appears. 'done' (default): when this one is completed. 'schedule': on this one's due date. */
  create?: 'done' | 'schedule';
}

export interface TaskComment {
  id: UUID;
  task_id: UUID;
  author_id: UUID;
  body: string;
  mentions: UUID[];
  created_at: ISODateTime;
  edited_at: ISODateTime | null;
  deleted_at: ISODateTime | null;
}

/** Names for the ids inside activity rows (t122u); only what the viewer may see. */
export interface ActivityRefs {
  users: Record<UUID, string>;
  teams: Record<UUID, string>;
  statuses: Record<UUID, string>;
  milestones: Record<UUID, string>;
  locations: Record<UUID, { name: string; archived: boolean }>;
  tasks: Record<UUID, { key: string; title: string }>;
  projects: Record<UUID, string>;
  /** First line of each comment, mentions as @Name. Deleted comments are absent. */
  comments: Record<UUID, string>;
}

export interface TaskActivity {
  id: number;
  actor_id: UUID | null;
  action: string;
  changes: Record<string, unknown>;
  created_at: ISODateTime;
}

export interface TaskDetail extends TaskSummary {
  description: string;
  estimate_minutes: number | null;
  created_by: UUID;
  created_at: ISODateTime;
  source_message_id: UUID | null;
  watcher_ids: UUID[];
  depends_on: { id: UUID; key: string; title: string; completed: boolean }[];
  blocking: { id: UUID; key: string; title: string; completed: boolean }[];
  subtasks: TaskSummary[];
  custom_values: Record<UUID, unknown>;
  attachments: AttachmentSummary[];
  links: { conversation_id: UUID; message_id: UUID | null; conversation_title: string | null }[];
}

export interface UpdateTaskInput {
  version: number;
  title?: string;
  description?: string;
  status_id?: UUID;
  priority?: TaskPriority;
  milestone_id?: UUID | null;
  start_date?: ISODate | null;
  due_date?: ISODate | null;
  position?: number;
  estimate_minutes?: number | null;
  area_id?: UUID | null;
  budget_pence?: number | null;
  parent_task_id?: UUID | null;
  recurrence?: RecurrenceRule | null;
  assignee_ids?: UUID[];
  team_ids?: UUID[];
  location_id?: UUID | null;
  watcher_ids?: UUID[];
  custom_values?: Record<UUID, unknown>;
  is_private?: boolean;
  due_time?: string | null;
  reminder_minutes?: number | null;
  /** Shortcut for the project's first Done (true) or To do (false) status. */
  done?: boolean;
}

/** Live status chip for a task created from / linked to a chat message. */
export interface MessageTaskLink {
  message_id: UUID;
  task_id: UUID;
  key: string;
  title: string;
  project_id: UUID;
  project_name: string;
  /** A task without a project (its project is the standalone one): show no project name. */
  no_project?: boolean;
  status_name: string;
  status_color: string;
  status_category: StatusCategory;
  completed_at: ISODateTime | null;
  due_date: ISODate | null;
  assignee_ids: UUID[];
  /** t641u: who turned the message into a task (or linked it); null if unknown or their account was removed. */
  linked_by?: UUID | null;
  linked_by_name?: string | null;
  /** t641u: true when the task was created from this message (not just linked to it later). */
  from_message?: boolean;
}

export type MyTask = TaskSummary & { project_name: string; assigned_directly: boolean; via_team_ids: UUID[] };

export interface SavedView {
  id: UUID;
  project_id: UUID | null;
  name: string;
  view_type: 'list' | 'kanban' | 'calendar';
  filters: Record<string, unknown>;
  sort: unknown[];
  is_shared: boolean;
  owner_id: UUID;
}

export interface AuditEntry {
  id: number;
  actor_id: UUID | null;
  actor_name: string | null;
  action: string;
  target_type: string | null;
  target_id: string | null;
  metadata: Record<string, unknown>;
  created_at: ISODateTime;
}

/** Event envelope published on Realtime Broadcast topics. */
export interface RealtimeEnvelope<T = unknown> {
  outbox_id?: number;
  type: string;
  payload: T;
}

// ---- People: birthdays & kudos (backlog #4, #5) ----
export interface BirthdaysResponse {
  date: string;
  today: { user_id: UUID; display_name: string; day: number; month: number; date: string; days_until: number }[];
  upcoming: { user_id: UUID; display_name: string; day: number; month: number; date: string; days_until: number }[];
}
/** Badge key (per workspace). Built-ins: thank_you, teamwork, above_and_beyond, customer_hero, safety_star; system: birthday, target_hit. */
export type KudosBadge = string;
export interface KudosBadgeDef {
  id: UUID; key: string; name: string; emoji: string; color: string; description: string | null;
  sort_order: number; system: boolean; retired_at: ISODateTime | null; usage_count?: number;
}
export interface KudosPost {
  id: UUID;
  workspace_id: UUID;
  author_id: UUID | null;
  /** t247u: who gave it (null for birthday / sales-target posts). */
  author_name?: string | null;
  kind: 'kudos' | 'birthday' | 'sales_target';
  badge: KudosBadge | null;
  body: string;
  created_at: ISODateTime;
  edited_at: ISODateTime | null;
  recipients: { user_id: UUID; display_name: string }[];
  teams: { team_id: UUID; name: string; kind: 'department' | 'group' }[];
  reactions: { emoji: string; count: number; user_ids: UUID[] }[];
  comment_count: number;
  attachments: { id: UUID; file_name: string; content_type: string; size_bytes: number; caption: string | null; width?: number | null; height?: number | null }[];
}
export interface KudosComment { id: UUID; author_id: UUID; body: string; created_at: ISODateTime }

// ---------------------------------------------------------------- AI assistant (backlog #8)
export type AssistantSourceType = 'message' | 'task' | 'attachment' | 'building' | 'article' | 'announcement' | 'policy';
export interface AssistantSource {
  /** 1-based citation number used in the answer as [n] */
  n: number;
  type: AssistantSourceType;
  id: UUID;
  title: string;
  snippet: string;
  link: string;
  author_name?: string | null;
  at?: ISODateTime | null;
  context?: string | null;
}
export interface AssistantAnswer {
  query_id: UUID;
  mode: 'llm' | 'search';
  retrieval: 'fts' | 'hybrid';
  answer: string | null;
  sources: AssistantSource[];
  cited: number[];
  model: string | null;
  notice: string | null;
  remaining_this_hour: number;
}
export interface AssistantStatus {
  mode: 'llm' | 'search';
  model: string | null;
  retrieval: 'fts' | 'hybrid';
  queries_per_hour: number;
  used_this_hour: number;
}
export interface AssistantQueryLog {
  id: UUID;
  user_id: UUID;
  display_name: string;
  question: string;
  origin: 'panel' | 'page' | 'slash';
  mode: 'llm' | 'search';
  status: 'ok' | 'no_results' | 'llm_error' | 'rate_limited';
  model: string | null;
  retrieval: 'fts' | 'hybrid';
  source_refs: { type: AssistantSourceType; id: UUID }[];
  cited_count: number;
  latency_ms: number;
  created_at: ISODateTime;
}

/** Inline thread preview for a top-level message (backlog #33). replies are the newest few, ascending by sequence. */
export interface ThreadPreview {
  message_id: string;
  reply_count: number;
  last_reply_at: string | null;
  /** Distinct repliers, most recent first (up to 5). */
  participant_ids: string[];
  replies: Message[];
  /** Pass as before= to GET /messages/:id/replies for older replies; null when everything is loaded. */
  next_before: number | null;
}
export interface ThreadRepliesPage { replies: Message[]; next_before: number | null }
export interface ThreadViewPrefs { all_collapsed: boolean; toggled_ids: string[] }
